/**
 * ReturnToStreetcarButton — NAV-06 persistent escape hatch to boarding.
 *
 * Plan 05-02 Task 2. A small HUD button fixed at top-right that is visible
 * ONLY in freeWalk or guidedTour. Clicking it dispatches {type:'REBOARD'}
 * via useNavSend; the machine transitions back to streetcarRiding (machine.ts
 * freeWalk → streetcarRiding transition) and the user rides the spline again
 * from the Birmingham platform.
 *
 * Design rationale:
 *   - Visibility gate matches the machine's REBOARD transition source set.
 *     Machine.ts only defines REBOARD on freeWalk (guidedTour exits first to
 *     freeWalk via EXIT_TOUR then can reboard — or, as an authoring
 *     simplification, we expose the button from guidedTour too so users who
 *     lose interest mid-tour have a single-click return path).
 *   - Hidden during streetcarRiding / boarding / arrivalCinematic — mid-ride
 *     REBOARD would be meaningless (they're already on the streetcar) and
 *     would also create a machine-state inconsistency since those states
 *     don't define REBOARD. Defense in depth alongside the machine.
 *   - Hidden in photoCompare because that's a full-screen overlay; the
 *     escape pattern there is CLOSE_COMPARE first.
 *   - Hidden in idle because BoardingOverlay owns the first interaction;
 *     a second "return" button when the user hasn't gone anywhere yet would
 *     be confusing.
 *
 * Positioning: fixed top-right, 1rem from top and right edges. The minimap
 * (Plan 05-04) goes bottom-right; EraToggle is bottom-center; SourcesPanel
 * is right-side vertical. Top-right keeps the HUD symmetric and gives the
 * Return button its own quadrant away from scene-inspection controls.
 *
 * Chrome: AESTHETIC-BRIEF Rule 7 — Goudy Old Style / Cormorant Garamond /
 * Georgia serif on cream #FDF6EE with espresso #3B2F2F text and a 1px
 * warm-ochre #D4A87E border. Matches BoardingOverlay + EraToggle.
 *
 * REBOARD snap-behavior: Phase 5 accepts the hard cut to streetcarRiding
 * (Pitfall 9 — documented). Phase 7 may add a 200ms fade bookend for polish;
 * the machine transition itself is instant and the user is now riding the
 * curve again from t=0.
 */
import { useNavStore } from '../store/useNavStore';
import { useNavSend } from '../navigation/NavContext';

const SERIF_STACK =
  '"Goudy Old Style", "Cormorant Garamond", Georgia, serif';
const CREAM = '#FDF6EE';
const ESPRESSO = '#3B2F2F';
const WARM_OCHRE = '#D4A87E';

export function ReturnToStreetcarButton() {
  const navState = useNavStore((s) => s.navState);
  const send = useNavSend();

  // NAV-06 visibility gate — see doc block above for rationale on each
  // excluded state. freeWalk + guidedTour only.
  if (navState !== 'freeWalk' && navState !== 'guidedTour') return null;

  const handleClick = () => {
    send({ type: 'REBOARD' });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Return to Streetcar"
      style={{
        position: 'fixed',
        top: '1rem',
        right: '1rem',
        zIndex: 40,
        padding: '10px 20px',
        background: CREAM,
        color: ESPRESSO,
        border: `1px solid ${WARM_OCHRE}`,
        fontFamily: SERIF_STACK,
        fontSize: '0.9rem',
        letterSpacing: '0.05em',
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        minWidth: 180,
      }}
    >
      Return to Streetcar
    </button>
  );
}
